<template>
    <section class="dp-layout psychology-report-page">
        <!-- 左侧学校树 -->
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" pane-title="组织架构" @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>

        <!-- 右侧报表统计分析主区 -->
        <section class="dp-right" v-loading="loading">
            <div v-if="hasSchoolSelected" class="report-content-box">
                <!-- 顶部时间与校区筛选 -->
                <div class="filter-bar">
                    <div class="filter-left">
                        <span class="label">当前范围：</span>
                        <span class="value" style="margin-right: 14px;">{{ schoolName || '请选择左侧部门' }}</span>
                        <el-button type="primary" size="small" icon="el-icon-search" @click="loadReport">查询统计</el-button>
                        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
                    </div>
                </div>

                <!-- 核心指标统计卡片网格 -->
                <div class="stat-card-grid">
                    <!-- 1. 在册档案总数 -->
                    <div class="stat-card stat-card--clickable" @click="openDetailDialog('archive_all')">
                        <div class="stat-icon stat-icon--blue">
                            <i class="el-icon-document"></i>
                        </div>
                        <div class="stat-meta">
                            <div class="stat-title">
                                <span>心理档案总人数</span>
                                <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                            </div>
                            <div class="stat-value">
                                {{ reportData.TotalArchiveCount || 0 }}
                                <span class="sub-unit">人建档</span>
                            </div>
                            <div class="stat-hint">全校在册心理档案 · 点击看明细</div>
                        </div>
                    </div>

                    <!-- 2. 待心理评估 -->
                    <div class="stat-card stat-card--clickable" @click="openDetailDialog('archive_focus')">
                        <div class="stat-icon stat-icon--orange">
                            <i class="el-icon-warning"></i>
                        </div>
                        <div class="stat-meta">
                            <div class="stat-title">
                                <span>待心理评估</span>
                                <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                            </div>
                            <div class="stat-value stat-value--warning">
                                {{ reportData.PendingPsychCount || 0 }}
                                <span class="sub-unit">人</span>
                            </div>
                            <div class="stat-hint">班主任筛查命中待评估 · 点击看明细</div>
                        </div>
                    </div>

                    <!-- 3. 心理预约申请总量 -->
                    <div class="stat-card stat-card--clickable" @click="openDetailDialog('appointment_all')">
                        <div class="stat-icon stat-icon--cyan">
                            <i class="el-icon-date"></i>
                        </div>
                        <div class="stat-meta">
                            <div class="stat-title">
                                <span>心理预约申请总量</span>
                                <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                            </div>
                            <div class="stat-value">
                                {{ reportData.TotalAppointmentCount || 0 }}
                                <span class="sub-unit">次发起</span>
                            </div>
                            <div class="stat-hint">班主任累计发起咨询预约 · 点击看明细</div>
                        </div>
                    </div>

                    <!-- 4. 已完成咨询签到 -->
                    <div class="stat-card stat-card--clickable" @click="openDetailDialog('appointment_completed')">
                        <div class="stat-icon stat-icon--green">
                            <i class="el-icon-circle-check"></i>
                        </div>
                        <div class="stat-meta">
                            <div class="stat-title">
                                <span>已完成咨询签到</span>
                                <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                            </div>
                            <div class="stat-value stat-value--success">
                                {{ reportData.CompletedAppointmentCount || 0 }}
                                <span class="sub-unit">/ {{ reportData.TotalAppointmentCount || 0 }} 预约</span>
                            </div>
                            <div class="stat-hint">
                                签到率：{{ calculateRate(reportData.CompletedAppointmentCount, reportData.TotalAppointmentCount) }} · 点击看明细
                            </div>
                        </div>
                    </div>

                    <!-- 5. 三表填写进度 -->
                    <div class="stat-card stat-card--clickable" @click="openDetailDialog('archive_fill')">
                        <div class="stat-icon stat-icon--purple">
                            <i class="el-icon-tickets"></i>
                        </div>
                        <div class="stat-meta">
                            <div class="stat-title">
                                <span>家长已填 / 班主任已填</span>
                                <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                            </div>
                            <div class="stat-value">
                                {{ reportData.ParentFilledCount || 0 }} / {{ reportData.HomeroomFilledCount || 0 }}
                                <span class="sub-unit">人</span>
                            </div>
                            <div class="stat-hint">家长 {{ reportData.ParentFilledRate || 0 }}% · 班主任 {{ reportData.HomeroomFilledRate || 0 }}% · 点击看谁已填</div>
                        </div>
                    </div>
                </div>

                <!-- 两个分析 Tab：等级分布概况 & 班级明细对比 -->
                <el-tabs v-model="activeTab" class="report-tabs">
                    <!-- Tab 1: 学生心理健康关注等级分布概况 -->
                    <el-tab-pane label="学生心理健康关注等级分布概况" name="distribution">
                        <div class="distribution-pane-scroll">
                            <div class="pane-tip-bar">
                                <i class="el-icon-info"></i>
                                <span>危机等级仅由心理老师表回写：一类 / 二类 / 三类。未评估学生不计入等级分布。</span>
                            </div>

                            <!-- 三个等级的分布卡片网格 -->
                            <div class="level-cards-grid">
                                <!-- 一类 -->
                                <div class="level-detail-card level-card--normal level-detail-card--clickable" @click="openDetailDialog('archive_normal')">
                                    <div class="level-card__header">
                                        <div class="level-badge level-badge--normal">
                                            <i class="el-icon-circle-check"></i>
                                            <span>一类</span>
                                            <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                                        </div>
                                        <span class="level-ratio-text">
                                            占比 {{ calculateRate(reportData.NormalCount, reportData.TotalArchiveCount) }}
                                        </span>
                                    </div>
                                    <div class="level-card__body">
                                        <div class="level-count-box">
                                            <span class="count-num">{{ reportData.NormalCount || 0 }}</span>
                                            <span class="count-unit">人</span>
                                            <span class="level-click-hint">点击看明细</span>
                                        </div>
                                        <el-progress
                                            :percentage="calculatePercentage(reportData.NormalCount, reportData.TotalArchiveCount)"
                                            status="success"
                                            :stroke-width="8"
                                            :show-text="false"
                                        />
                                        <div class="level-desc">
                                            心理老师评估为一类危机。处理指引以校区配置及提交时快照为准。
                                        </div>
                                    </div>
                                </div>

                                <!-- 二类 -->
                                <div class="level-detail-card level-card--attention level-detail-card--clickable" @click="openDetailDialog('archive_attention')">
                                    <div class="level-card__header">
                                        <div class="level-badge level-badge--attention">
                                            <i class="el-icon-bell"></i>
                                            <span>二类</span>
                                            <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                                        </div>
                                        <span class="level-ratio-text">
                                            占比 {{ calculateRate(reportData.AttentionCount, reportData.TotalArchiveCount) }}
                                        </span>
                                    </div>
                                    <div class="level-card__body">
                                        <div class="level-count-box">
                                            <span class="count-num count-num--warning">{{ reportData.AttentionCount || 0 }}</span>
                                            <span class="count-unit">人</span>
                                            <span class="level-click-hint">点击看明细</span>
                                        </div>
                                        <el-progress
                                            :percentage="calculatePercentage(reportData.AttentionCount, reportData.TotalArchiveCount)"
                                            color="#E6A23C"
                                            :stroke-width="8"
                                            :show-text="false"
                                        />
                                        <div class="level-desc">
                                            心理老师评估为二类危机。处理指引以校区配置及提交时快照为准。
                                        </div>
                                    </div>
                                </div>

                                <!-- 三类 -->
                                <div class="level-detail-card level-card--crisis level-detail-card--clickable" @click="openDetailDialog('archive_crisis')">
                                    <div class="level-card__header">
                                        <div class="level-badge level-badge--crisis">
                                            <i class="el-icon-warning"></i>
                                            <span>三类</span>
                                            <i class="el-icon-arrow-right card-enter-icon" title="点击查看明细"></i>
                                        </div>
                                        <span class="level-ratio-text">
                                            占比 {{ calculateRate(reportData.CrisisCount, reportData.TotalArchiveCount) }}
                                        </span>
                                    </div>
                                    <div class="level-card__body">
                                        <div class="level-count-box">
                                            <span class="count-num count-num--danger">{{ reportData.CrisisCount || 0 }}</span>
                                            <span class="count-unit">人</span>
                                            <span class="level-click-hint">点击看明细</span>
                                        </div>
                                        <el-progress
                                            :percentage="calculatePercentage(reportData.CrisisCount, reportData.TotalArchiveCount)"
                                            status="exception"
                                            :stroke-width="8"
                                            :show-text="false"
                                        />
                                        <div class="level-desc">
                                            心理老师评估为三类危机。处理指引以校区配置及提交时快照为准。
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 整体比例进度对比概览卡片 -->
                            <div class="summary-progress-card">
                                <div class="card-title">
                                    <i class="el-icon-data-line"></i>
                                    <span>全校学生心理健康关注等级分布比例图</span>
                                </div>
                                <div class="ratio-bars-container">
                                    <!-- 一类 -->
                                    <div class="ratio-bar-row ratio-bar-row--clickable" @click="openDetailDialog('archive_normal')">
                                        <div class="ratio-bar-tag tag--normal">
                                            <i class="el-icon-circle-check"></i>
                                            <span>一类</span>
                                        </div>
                                        <div class="ratio-bar-track">
                                            <el-progress
                                                :percentage="calculatePercentage(reportData.NormalCount, reportData.TotalArchiveCount)"
                                                :show-text="false"
                                                color="#67C23A"
                                                :stroke-width="12"
                                            />
                                        </div>
                                        <div class="ratio-bar-stat">
                                            <span class="stat-count">{{ reportData.NormalCount || 0 }}</span>
                                            <span class="stat-unit">人</span>
                                            <span class="stat-divider">/</span>
                                            <span class="stat-rate">占比 {{ calculateRate(reportData.NormalCount, reportData.TotalArchiveCount) }}</span>
                                            <i class="el-icon-arrow-right card-enter-icon"></i>
                                        </div>
                                    </div>

                                    <!-- 二类 -->
                                    <div class="ratio-bar-row ratio-bar-row--clickable" @click="openDetailDialog('archive_attention')">
                                        <div class="ratio-bar-tag tag--attention">
                                            <i class="el-icon-warning-outline"></i>
                                            <span>二类</span>
                                        </div>
                                        <div class="ratio-bar-track">
                                            <el-progress
                                                :percentage="calculatePercentage(reportData.AttentionCount, reportData.TotalArchiveCount)"
                                                :show-text="false"
                                                color="#E6A23C"
                                                :stroke-width="12"
                                            />
                                        </div>
                                        <div class="ratio-bar-stat">
                                            <span class="stat-count stat-count--attention">{{ reportData.AttentionCount || 0 }}</span>
                                            <span class="stat-unit">人</span>
                                            <span class="stat-divider">/</span>
                                            <span class="stat-rate">占比 {{ calculateRate(reportData.AttentionCount, reportData.TotalArchiveCount) }}</span>
                                            <i class="el-icon-arrow-right card-enter-icon"></i>
                                        </div>
                                    </div>

                                    <!-- 三类 -->
                                    <div class="ratio-bar-row ratio-bar-row--clickable" @click="openDetailDialog('archive_crisis')">
                                        <div class="ratio-bar-tag tag--crisis">
                                            <i class="el-icon-circle-close"></i>
                                            <span>三类</span>
                                        </div>
                                        <div class="ratio-bar-track">
                                            <el-progress
                                                :percentage="calculatePercentage(reportData.CrisisCount, reportData.TotalArchiveCount)"
                                                :show-text="false"
                                                color="#F56C6C"
                                                :stroke-width="12"
                                            />
                                        </div>
                                        <div class="ratio-bar-stat">
                                            <span class="stat-count stat-count--crisis">{{ reportData.CrisisCount || 0 }}</span>
                                            <span class="stat-unit">人</span>
                                            <span class="stat-divider">/</span>
                                            <span class="stat-rate">占比 {{ calculateRate(reportData.CrisisCount, reportData.TotalArchiveCount) }}</span>
                                            <i class="el-icon-arrow-right card-enter-icon"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 2: 各班级心理档案、关注人群与业务量明细对比 -->
                    <el-tab-pane label="各班级心理档案、关注人群与业务量明细对比" name="department">
                        <div class="department-pane-wrapper">
                            <div class="pane-tip-bar">
                                <i class="el-icon-tickets"></i>
                                <span>共统计 {{ (reportData.DepartmentStats || []).length }} 个班级。对比各班三表填写进度、危机等级与预约人次。</span>
                            </div>

                            <div class="tbl-scroll-host">
                                <el-table
                                    :data="pagedDepartmentStats"
                                    border
                                    height="100%"
                                    style="width: 100%;"
                                    class="compact-table"
                                >
                                    <el-table-column type="index" :index="calcDeptIndex" label="序号" width="55" align="center" />
                                    <el-table-column prop="DepartmentName" label="班级名称" min-width="160" show-overflow-tooltip />
                                    <el-table-column prop="TotalStudentCount" label="档案人数" width="110" align="center" />
                                    <el-table-column prop="ParentFilledCount" label="家长已填" width="100" align="center" />
                                    <el-table-column prop="HomeroomFilledCount" label="班主任已填" width="110" align="center" />
                                    <el-table-column prop="PendingPsychCount" label="待评估" width="90" align="center" />
                                    <el-table-column prop="PsychEvaluatedCount" label="已评估" width="90" align="center" />
                                    <el-table-column label="评估完成率" width="110" align="center">
                                        <template slot-scope="scope">{{ (scope.row.PsychEvalRate || 0) + '%' }}</template>
                                    </el-table-column>
                                    <el-table-column prop="Level1Count" label="一类" width="80" align="center" />
                                    <el-table-column prop="Level2Count" label="二类" width="80" align="center" />
                                    <el-table-column prop="Level3Count" label="三类" width="80" align="center" />
                                    <el-table-column prop="AppointmentCount" label="发起预约人次" width="120" align="center" />
                                </el-table>
                            </div>

                            <!-- 班级明细表格底部分页条 -->
                            <div class="department-toolbar-wrap">
                                <el-pagination
                                    background
                                    layout="total, sizes, prev, pager, next, jumper"
                                    :current-page="deptPage"
                                    :page-sizes="[10, 20, 50, 100]"
                                    :page-size="deptPageSize"
                                    :total="(reportData.DepartmentStats || []).length"
                                    @size-change="handleDeptSizeChange"
                                    @current-change="handleDeptCurrentChange"
                                />
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-data-analysis"></i>
                    <p>请在左侧选择所属学校，查看学生心理档案与预约汇总报表</p>
                </div>
            </div>

            <!-- 汇总统计卡片点击明细弹窗 -->
            <el-dialog
                :title="detailDialogTitle"
                :visible.sync="detailDialogVisible"
                width="1100px"
                top="7vh"
                custom-class="summary-detail-dialog"
                :close-on-click-modal="false"
            >
                <div class="dialog-detail-container">
                    <!-- 弹窗筛选工具栏 -->
                    <div class="dialog-filter-bar">
                        <div class="filter-count-tip">
                            <i class="el-icon-info"></i>
                            <span>共查询到 <b>{{ detailTotal }}</b> 条记录</span>
                        </div>
                        <div class="filter-input-wrap">
                            <template v-if="detailType.startsWith('archive')">
                                <el-select
                                    v-model="detailParentStatus"
                                    placeholder="家长填写"
                                    size="small"
                                    clearable
                                    style="width: 120px; margin-right: 8px;"
                                    @change="handleDetailSearch"
                                >
                                    <el-option label="家长已填" :value="1" />
                                    <el-option label="家长未填" :value="0" />
                                </el-select>
                                <el-select
                                    v-model="detailHomeroomStatus"
                                    placeholder="班主任填写"
                                    size="small"
                                    clearable
                                    style="width: 130px; margin-right: 8px;"
                                    @change="handleDetailSearch"
                                >
                                    <el-option label="班主任已填" :value="1" />
                                    <el-option label="班主任未填" :value="0" />
                                </el-select>
                            </template>
                            <el-input
                                v-model="detailSearchKeyword"
                                placeholder="搜索学生姓名 / 班级"
                                size="small"
                                clearable
                                style="width: 220px;"
                                @keyup.enter.native="handleDetailSearch"
                                @clear="handleDetailSearch"
                            >
                                <el-button slot="append" icon="el-icon-search" @click="handleDetailSearch" />
                            </el-input>
                        </div>
                    </div>

                    <!-- 列表区域：按类型动态展示 -->
                    <!-- 1. 心理档案明细 -->
                    <div v-if="detailType.startsWith('archive')" class="dialog-table-container">
                        <el-table
                            :data="detailList"
                            v-loading="detailLoading"
                            border
                            size="small"
                            max-height="390"
                            style="width: 100%;"
                            class="compact-table"
                        >
                            <el-table-column type="index" label="序号" width="55" align="center" :index="calcIndex" />
                            <el-table-column prop="StudentName" label="学生姓名" min-width="120" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span class="detail-student-name">{{ scope.row.StudentName }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="StudentNumber" label="学号" width="110" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span>{{ scope.row.StudentNumber || scope.row.StuCode || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="Gender" label="性别" width="55" align="center">
                                <template slot-scope="scope">
                                    <span>{{ (scope.row.Gender === '男' || scope.row.Gender === '女') ? scope.row.Gender : '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="DepartmentName" label="所在班级" min-width="120" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span>{{ scope.row.DepartmentName || scope.row.ClassName || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="家长填写" width="90" align="center">
                                <template slot-scope="scope">
                                    <el-tag :type="Number(scope.row.ParentStatus) === 1 ? 'success' : 'info'" size="mini">
                                        {{ Number(scope.row.ParentStatus) === 1 ? '已填' : '未填' }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="班主任填写" width="100" align="center">
                                <template slot-scope="scope">
                                    <el-tag :type="Number(scope.row.HomeroomStatus) === 1 ? 'success' : 'info'" size="mini">
                                        {{ Number(scope.row.HomeroomStatus) === 1 ? '已填' : '未填' }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="AttentionLevel" label="危机等级" width="90" align="center">
                                <template slot-scope="scope">
                                    <el-tag v-if="scope.row.AttentionLevel === 3" type="danger" size="mini" effect="dark">三类</el-tag>
                                    <el-tag v-else-if="scope.row.AttentionLevel === 2" type="warning" size="mini" effect="dark">二类</el-tag>
                                    <el-tag v-else-if="scope.row.AttentionLevel === 1" type="success" size="mini">一类</el-tag>
                                    <span v-else class="text-muted">未评估</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="Status" label="档案状态" width="90" align="center">
                                <template slot-scope="scope">
                                    <el-tag v-if="Number(scope.row.Status) === 2" type="info" size="mini">已结案</el-tag>
                                    <el-tag v-else type="success" size="mini">正常在档</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="TotalCounselingCount" label="咨询次数" width="80" align="center" />
                        </el-table>
                    </div>

                    <!-- 2. 心理预约明细 (预约申请总量 / 已完成咨询签到) -->
                    <div v-else-if="detailType.startsWith('appointment')" class="dialog-table-container">
                        <el-table
                            :data="detailList"
                            v-loading="detailLoading"
                            border
                            size="small"
                            max-height="390"
                            style="width: 100%;"
                            class="compact-table"
                        >
                            <el-table-column type="index" label="序号" width="55" align="center" :index="calcIndex" />
                            <el-table-column prop="StudentName" label="学生姓名" width="105">
                                <template slot-scope="scope">
                                    <span class="detail-student-name">{{ scope.row.StudentName }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="CampusName" label="所属校区" width="110" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <el-tag v-if="scope.row.CampusName && scope.row.CampusName !== '-'" size="mini" type="info">{{ scope.row.CampusName }}</el-tag>
                                    <span v-else>-</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="DepartmentName" label="所在班级" min-width="130" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span>{{ scope.row.DepartmentName || scope.row.ClassName || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="AppointDate" label="预约日期" width="110" align="center">
                                <template slot-scope="scope">
                                    <span>{{ formatDate(scope.row.AppointDate) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="预约时段" width="150" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span v-if="scope.row.TimeMode === 1">{{ scope.row.SpecialCourseTimeName || '特殊课程联动' }}</span>
                                    <span v-else>{{ scope.row.StartTime || scope.row.CustomStartTime || '-' }} ~ {{ scope.row.EndTime || scope.row.CustomEndTime || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="Status" label="预约状态" width="100" align="center">
                                <template slot-scope="scope">
                                    <el-tag
                                        :type="getAppointmentStatusTagType(scope.row.Status)"
                                        :effect="Number(scope.row.Status) === 3 ? 'dark' : 'plain'"
                                        size="mini"
                                    >
                                        {{ formatAppointmentStatusText(scope.row.Status) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="CreatedTeacherName" label="发起班主任" width="110">
                                <template slot-scope="scope">
                                    <span>{{ scope.row.ApplyTeacherName || scope.row.CreatedTeacherName || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="ConsultReason" label="咨询事由" min-width="140" show-overflow-tooltip>
                                <template slot-scope="scope">
                                    <span>{{ scope.row.Reason || scope.row.ConsultReason || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="AuditTeacherName" label="审核心理老师" width="105">
                                <template slot-scope="scope">
                                    <span>{{ scope.row.AuditTeacherName || '-' }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="CheckInTime" label="签到时间" width="145" align="center">
                                <template slot-scope="scope">
                                    <span>{{ scope.row.CheckInTime || '-' }}</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>

                    <!-- 分页组件：每页固定 8 条 -->
                    <div class="dialog-pagination-box">
                        <el-pagination
                            background
                            layout="total, prev, pager, next, jumper"
                            :current-page="detailPage"
                            :page-size="detailPageSize"
                            :total="detailTotal"
                            @current-change="handleDetailPageChange"
                        />
                    </div>
                </div>
                <div slot="footer" class="dialog-footer">
                    <el-button size="small" @click="detailDialogVisible = false">关 闭</el-button>
                </div>
            </el-dialog>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import {
    getPsychologySummaryReport,
    getPsychologyArchiveList,
    getPsychologyAppointments
} from '../../api/modules/psychology';
import {
    resolveActiveSchoolId,
    normalizeDeptId,
    treeNodeDeptId,
    deptIdsEqual,
} from '../../utils/deptCascaderProps';

export default {
    name: 'PsychologyReport',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: '',
            schoolName: '',
            departmentId: '',
            activeTab: 'distribution',
            loading: false,
            reportData: {
                TotalArchiveCount: 0,
                TotalStudents: 0,
                ParentFilledCount: 0,
                ParentFilledRate: 0,
                HomeroomFilledCount: 0,
                HomeroomFilledRate: 0,
                PendingPsychCount: 0,
                PsychEvaluatedCount: 0,
                PsychEvalRate: 0,
                Level1Count: 0,
                Level2Count: 0,
                Level3Count: 0,
                NormalCount: 0,
                AttentionCount: 0,
                CrisisCount: 0,
                TotalAppointmentCount: 0,
                CompletedAppointmentCount: 0,
                DepartmentStats: []
            },
            // 点击汇总指标卡片弹出明细弹窗状态
            detailDialogVisible: false,
            detailDialogTitle: '',
            detailType: '', // 'archive_all' | 'archive_fill' | 'archive_focus' | 'archive_normal' | 'archive_crisis' | 'archive_attention' | 'appointment_all' | 'appointment_completed'
            detailLoading: false,
            detailList: [],
            detailPage: 1,
            detailPageSize: 8, // 严格按要求：一页8条
            detailTotal: 0,
            detailSearchKeyword: '',
            detailParentStatus: null, // 1已填 / 0未填 / null不限
            detailHomeroomStatus: null,

            // 班级明细表格分页状态
            deptPage: 1,
            deptPageSize: 10
        };
    },
    computed: {
        hasSchoolSelected() {
            return !!normalizeDeptId(this.schoolId);
        },
        pagedDepartmentStats() {
            const list = this.reportData.DepartmentStats || [];
            const start = (this.deptPage - 1) * this.deptPageSize;
            return list.slice(start, start + this.deptPageSize);
        }
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            const id = normalizeDeptId(newSchoolId);
            if (id && !deptIdsEqual(id, this.schoolId)) {
                this.schoolId = id;
                this.departmentId = '';
                this.loadReport();
            }
        }
    },
    methods: {
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            return normalizeDeptId(this.schoolId);
        },

        treeNodeClick(data, node) {
            if (!data) return;
            const orgType = String((data.DeptOrgType || data.deptOrgType) || '').toLowerCase();
            const activeSchoolId = this.resolveSchoolId();
            const isSchoolNode = orgType === 'school' || orgType === 'affiliateschool'
                || (!orgType && (Number(data.type) === 1 || deptIdsEqual(data.value, activeSchoolId)));
            if (isSchoolNode) {
                this.schoolId = treeNodeDeptId(data) || activeSchoolId;
                this.schoolName = data.label || '';
                this.departmentId = '';
            } else {
                this.departmentId = treeNodeDeptId(data);
                let tempNode = node;
                let foundSchoolId = '';
                while (tempNode) {
                    const pType = String((tempNode.data && (tempNode.data.DeptOrgType || tempNode.data.deptOrgType)) || '').toLowerCase();
                    if (tempNode.data && (pType === 'school' || pType === 'affiliateschool')) {
                        foundSchoolId = treeNodeDeptId(tempNode.data);
                        break;
                    }
                    tempNode = tempNode.parent;
                }
                this.schoolId = foundSchoolId || activeSchoolId || normalizeDeptId(this.schoolId);
                this.schoolName = data.label || this.schoolName;
            }
            this.loadReport();
        },

        async loadReport() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const params = {
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0
                };
                const res = await getPsychologySummaryReport(params);
                if (res.data && res.data.success) {
                    const overview = res.data.response || {};
                    const stats = overview.DepartmentStats || [];
                    this.reportData = {
                        TotalArchiveCount: overview.TotalArchiveCount || overview.TotalStudents || 0,
                        TotalStudents: overview.TotalStudents || overview.TotalArchiveCount || 0,
                        ParentFilledCount: overview.ParentFilledCount || 0,
                        ParentFilledRate: overview.ParentFilledRate || 0,
                        HomeroomFilledCount: overview.HomeroomFilledCount || 0,
                        HomeroomFilledRate: overview.HomeroomFilledRate || 0,
                        PendingPsychCount: overview.PendingPsychCount || 0,
                        PsychEvaluatedCount: overview.PsychEvaluatedCount || 0,
                        PsychEvalRate: overview.PsychEvalRate || 0,
                        Level1Count: overview.Level1Count || 0,
                        Level2Count: overview.Level2Count || 0,
                        Level3Count: overview.Level3Count || 0,
                        NormalCount: overview.Level1Count || 0,
                        AttentionCount: overview.Level2Count || 0,
                        CrisisCount: overview.Level3Count || 0,
                        TotalAppointmentCount: overview.TotalAppointmentCount || 0,
                        CompletedAppointmentCount: overview.CompletedAppointmentCount || 0,
                        DepartmentStats: Array.isArray(stats) ? stats : []
                    };
                    this.deptPage = 1;
                }
            } catch (err) {
                this.$message.error('加载心理报表数据失败');
            } finally {
                this.loading = false;
            }
        },

        handleReset() {
            this.deptPage = 1;
            this.loadReport();
        },

        calcDeptIndex(index) {
            return (this.deptPage - 1) * this.deptPageSize + index + 1;
        },

        handleDeptSizeChange(val) {
            this.deptPageSize = val;
            this.deptPage = 1;
        },

        handleDeptCurrentChange(val) {
            this.deptPage = val;
        },

        calculateRate(count, total) {
            if (!total || total === 0) return '0%';
            const rate = ((count || 0) / total) * 100;
            return rate.toFixed(1) + '%';
        },

        calculatePercentage(count, total) {
            if (!total || total === 0) return 0;
            const rate = ((count || 0) / total) * 100;
            return Number(Math.min(100, Math.max(0, rate)).toFixed(1));
        },

        // ================= 点击汇总卡片展开明细 =================

        openDetailDialog(type) {
            if (!this.schoolId) {
                this.$message.warning('请先在左侧选择所属学校');
                return;
            }

            const titleMap = {
                archive_all: '建立心理档案学生明细',
                archive_fill: '家长 / 班主任填写明细',
                archive_focus: '待心理评估学生明细',
                archive_normal: '一类学生明细',
                archive_crisis: '三类学生明细',
                archive_attention: '二类学生明细',
                appointment_all: '心理预约申请明细列表',
                appointment_completed: '已完成咨询签到预约明细'
            };

            this.detailType = type;
            this.detailDialogTitle = titleMap[type] || '指标明细列表';
            this.detailPage = 1;
            this.detailSearchKeyword = '';
            this.detailParentStatus = null;
            this.detailHomeroomStatus = null;
            this.detailList = [];
            this.detailTotal = 0;
            this.detailDialogVisible = true;

            this.loadDetailList();
        },

        /** 组装档案明细查询参数（含家长/班主任填写筛选） */
        buildArchiveDetailParams(extra = {}) {
            const params = {
                page: this.detailPage,
                intPageSize: this.detailPageSize,
                schoolId: this.schoolId,
                departmentId: this.departmentId || 0,
                keyword: this.detailSearchKeyword,
                ...extra
            };
            if (this.detailParentStatus === 0 || this.detailParentStatus === 1) {
                params.parentStatus = this.detailParentStatus;
            }
            if (this.detailHomeroomStatus === 0 || this.detailHomeroomStatus === 1) {
                params.homeroomStatus = this.detailHomeroomStatus;
            }
            return params;
        },

        async loadDetailList() {
            this.detailLoading = true;
            try {
                const startDate = null;
                const endDate = null;

                if (this.detailType === 'archive_all' || this.detailType === 'archive_fill') {
                    const res = await getPsychologyArchiveList(this.buildArchiveDetailParams({ status: 1 }));
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'archive_normal') {
                    const res = await getPsychologyArchiveList(this.buildArchiveDetailParams({
                        attentionLevel: 1,
                        status: 1
                    }));
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'archive_focus') {
                    const res = await getPsychologyArchiveList(this.buildArchiveDetailParams({
                        psychReviewStatus: 1
                    }));
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'archive_crisis') {
                    const res = await getPsychologyArchiveList(this.buildArchiveDetailParams({
                        attentionLevel: 3,
                        status: 1
                    }));
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'archive_attention') {
                    const res = await getPsychologyArchiveList(this.buildArchiveDetailParams({
                        attentionLevel: 2,
                        status: 1
                    }));
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'appointment_all') {
                    // 全部预约申请
                    const res = await getPsychologyAppointments({
                        page: this.detailPage,
                        intPageSize: this.detailPageSize,
                        schoolId: this.schoolId,
                        departmentId: this.departmentId || 0,
                        startDate,
                        endDate,
                        keyword: this.detailSearchKeyword
                    });
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                } else if (this.detailType === 'appointment_completed') {
                    // 已完成咨询签到预约 (Status = 3)
                    const res = await getPsychologyAppointments({
                        page: this.detailPage,
                        intPageSize: this.detailPageSize,
                        schoolId: this.schoolId,
                        departmentId: this.departmentId || 0,
                        status: 3,
                        startDate,
                        endDate,
                        keyword: this.detailSearchKeyword
                    });
                    if (res.data && res.data.success) {
                        const pageData = res.data.response || {};
                        this.detailList = pageData.data || [];
                        this.detailTotal = pageData.dataCount || 0;
                    }
                }
            } catch (err) {
                this.$message.error('加载指标明细数据失败');
            } finally {
                this.detailLoading = false;
            }
        },

        handleDetailSearch() {
            this.detailPage = 1;
            this.loadDetailList();
        },

        handleDetailPageChange(val) {
            this.detailPage = val;
            this.loadDetailList();
        },

        calcIndex(index) {
            return (this.detailPage - 1) * this.detailPageSize + index + 1;
        },

        formatDate(val) {
            if (!val) return '-';
            const s = String(val);
            return s.length >= 10 ? s.substring(0, 10) : s;
        },

        formatAppointmentStatusText(status) {
            const num = Number(status);
            switch (num) {
                case 0: return '待审核';
                case 1: return '已同意';
                case 2: return '已拒绝';
                case 3: return '已签到';
                case 4: return '已取消';
                default: return '已取消';
            }
        },

        getAppointmentStatusTagType(status) {
            const num = Number(status);
            switch (num) {
                case 0: return 'warning';
                case 1: return 'primary';
                case 2: return 'danger';
                case 3: return 'success';
                case 4: return 'info';
                default: return 'info';
            }
        }
    },
    mounted() {
        const defaultSchoolId = this.resolveSchoolId();
        if (defaultSchoolId) {
            this.schoolId = defaultSchoolId;
        }
        // 报表由左侧教学树首次点击加载，避免与 DepartmentPane 初始化重复请求
    }
};
</script>

<style scoped>
.report-content-box {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.filter-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 6px;
    margin-bottom: 8px;
}

.filter-left {
    display: flex;
    align-items: center;
}

.filter-left .label {
    font-size: 14px;
    color: #606266;
    margin-right: 6px;
}

.stat-card-grid {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
    margin-bottom: 8px;
}

.stat-card {
    background: #ffffff;
    border: 1px solid #e8edf5;
    border-radius: 6px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.stat-card--clickable {
    cursor: pointer;
    position: relative;
    transition: all 0.22s ease-in-out;
    user-select: none;
}

.stat-card--clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(64, 158, 255, 0.15);
    border-color: #b3d8ff;
}

.card-enter-icon {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 4px;
    transition: transform 0.2s, color 0.2s;
}

.stat-card--clickable:hover .card-enter-icon {
    color: #409eff;
    transform: translateX(3px);
}

.stat-icon {
    width: 42px;
    height: 42px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 12px;
    flex-shrink: 0;
}

.stat-icon--blue {
    background: #ecf5ff;
    color: #409eff;
}

.stat-icon--red {
    background: #fef0f0;
    color: #f56c6c;
}

.stat-icon--orange {
    background: #fdf6ec;
    color: #e6a23c;
}

.stat-icon--green {
    background: #f0f9eb;
    color: #67c23a;
}

.stat-icon--cyan {
    background: #e6f7ff;
    color: #13c2c2;
}

.stat-icon--purple {
    background: #f4ecf7;
    color: #9c27b0;
}

.stat-meta {
    flex: 1;
    min-width: 0;
}

.stat-title {
    font-size: 12px;
    color: #64748b;
}

.stat-value {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 2px 0 2px 0;
}

.sub-unit {
    font-size: 12px;
    font-weight: normal;
    color: #94a3b8;
}

.stat-value--danger {
    color: #f56c6c;
}

.stat-value--warning {
    color: #e6a23c;
}

.stat-value--success {
    color: #67c23a;
}

.stat-hint {
    font-size: 11px;
    color: #94a3b8;
}

.report-tabs {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e8edf5;
    border-radius: 6px;
    padding: 0 14px 10px 14px;
}

.report-tabs >>> .el-tabs__header {
    flex-shrink: 0;
    margin: 0 0 8px 0;
}

.report-tabs >>> .el-tabs__content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.report-tabs >>> .el-tab-pane {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.pane-tip-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 6px 12px;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 4px;
    font-size: 12px;
    color: #64748b;
    margin-bottom: 10px;
}

.pane-tip-bar i {
    color: #409eff;
    margin-right: 6px;
    font-size: 14px;
}

.distribution-pane-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
}

.level-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
    margin-bottom: 14px;
}

.level-detail-card {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px 16px;
    background: #fafbfc;
    transition: all 0.22s ease-in-out;
}

.level-detail-card--clickable {
    cursor: pointer;
    user-select: none;
    position: relative;
}

.level-detail-card--clickable:hover {
    transform: translateY(-2px);
}

.level-card--normal.level-detail-card--clickable:hover {
    border-color: #86efac;
    box-shadow: 0 4px 14px rgba(34, 197, 94, 0.16);
}

.level-card--attention.level-detail-card--clickable:hover {
    border-color: #fde047;
    box-shadow: 0 4px 14px rgba(234, 179, 8, 0.18);
}

.level-card--crisis.level-detail-card--clickable:hover {
    border-color: #fca5a5;
    box-shadow: 0 4px 14px rgba(239, 68, 68, 0.18);
}

.level-detail-card--clickable:hover .card-enter-icon {
    color: #409eff;
    transform: translateX(3px);
}

.level-click-hint {
    font-size: 11px;
    font-weight: normal;
    color: #94a3b8;
    margin-left: 8px;
    transition: color 0.2s;
}

.level-detail-card--clickable:hover .level-click-hint {
    color: #409eff;
}

.level-card--normal {
    border-color: #d1fae5;
    background: #f0fdf4;
}

.level-card--attention {
    border-color: #fef3c7;
    background: #fffbeb;
}

.level-card--crisis {
    border-color: #fee2e2;
    background: #fef2f2;
}

.level-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.level-badge {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
}

.level-badge i {
    margin-right: 4px;
    font-size: 15px;
}

.level-badge--normal {
    color: #16a34a;
}

.level-badge--attention {
    color: #d97706;
}

.level-badge--crisis {
    color: #dc2626;
}

.level-ratio-text {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.level-count-box {
    margin-bottom: 6px;
}

.count-num {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-right: 4px;
}

.count-num--warning {
    color: #d97706;
}

.count-num--danger {
    color: #dc2626;
}

.count-unit {
    font-size: 13px;
    color: #64748b;
}

.level-desc {
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
    margin-top: 8px;
}

.summary-progress-card {
    border: 1px solid #e8edf5;
    border-radius: 8px;
    padding: 16px 20px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.card-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
}

.card-title i {
    color: #409eff;
    margin-right: 6px;
    font-size: 15px;
}

.ratio-bars-container {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ratio-bar-row {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 6px;
    padding: 12px 18px;
    transition: all 0.2s ease;
}

.ratio-bar-row--clickable {
    cursor: pointer;
    user-select: none;
}

.ratio-bar-row--clickable:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateX(2px);
}

.ratio-bar-row--clickable:hover .card-enter-icon {
    color: #409eff;
    transform: translateX(3px);
}

.ratio-bar-tag {
    width: 130px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
}

.ratio-bar-tag i {
    margin-right: 6px;
    font-size: 14px;
}

.tag--normal {
    color: #16a34a;
}

.tag--attention {
    color: #d97706;
}

.tag--crisis {
    color: #dc2626;
}

.ratio-bar-track {
    flex: 1;
    min-width: 0;
    margin: 0 24px;
}

.ratio-bar-track >>> .el-progress-bar__outer {
    background-color: #e2e8f0;
}

.ratio-bar-stat {
    width: 170px;
    flex-shrink: 0;
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    font-size: 13px;
}

.stat-count {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
}

.stat-count--attention {
    color: #d97706;
}

.stat-count--crisis {
    color: #dc2626;
}

.stat-unit {
    font-size: 12px;
    color: #64748b;
    margin-left: 2px;
    margin-right: 10px;
}

.stat-divider {
    font-size: 12px;
    color: #cbd5e1;
    margin-right: 10px;
}

.stat-rate {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.department-pane-wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.tbl-scroll-host {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.department-toolbar-wrap {
    flex-shrink: 0;
    padding: 10px 0 2px 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

.attention-number {
    color: #e6a23c;
    font-weight: 600;
}

.crisis-number {
    color: #f56c6c;
    font-weight: 600;
}

.text-danger {
    color: #f56c6c;
    font-weight: 600;
}

.text-muted {
    color: #c0c4cc;
}

.no-department-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    color: #909399;
}

.no-department-tip i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #c0c4cc;
}

/* 汇总指标明细弹窗样式 */
.dialog-detail-container {
    display: flex;
    flex-direction: column;
}

.dialog-filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 6px;
    margin-bottom: 10px;
    flex-shrink: 0;
}

.filter-count-tip {
    font-size: 13px;
    color: #64748b;
    display: flex;
    align-items: center;
}

.filter-count-tip i {
    color: #409eff;
    margin-right: 6px;
    font-size: 14px;
}

.filter-count-tip b {
    color: #1e293b;
    margin: 0 2px;
}

.filter-input-wrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
}

.dialog-table-container {
    flex: 1;
    overflow: hidden;
}

.detail-student-name {
    font-weight: 600;
    color: #1e293b;
}

.dialog-pagination-box {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
}
</style>
